import { useEffect, useRef, useState } from "react";
import { moodColor } from "@/lib/emotions";

const hashSeed = (id) => {
  let h = 2166136261;
  for (const c of id) { h ^= c.charCodeAt(0); h = Math.imul(h, 16777619); }
  return h >>> 0;
};

const mulberry32 = (seed) => () => {
  seed = (seed + 0x6d2b79f5) | 0;
  let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
  t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
};

const rgba = (hex, a) => {
  const n = parseInt(hex.replace("#", ""), 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`;
};

const cssVar = (name) => getComputedStyle(document.documentElement).getPropertyValue(name).trim();

export const blobCount = (words) => Math.min(9, 3 + Math.floor(words / 60));

const useDarkScheme = () => {
  const query = "(prefers-color-scheme: dark)";
  const [dark, setDark] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const m = window.matchMedia(query);
    const onChange = (e) => setDark(e.matches);
    m.addEventListener("change", onChange);
    return () => m.removeEventListener("change", onChange);
  }, []);
  return dark;
};

function draw(canvas, { id, words, moods, width, height }) {
  const dpr = window.devicePixelRatio || 1;
  canvas.width = Math.round(width * dpr);
  canvas.height = Math.round(height * dpr);
  const ctx = canvas.getContext("2d");
  ctx.scale(dpr, dpr);
  ctx.fillStyle = cssVar("--superficie");
  ctx.fillRect(0, 0, width, height);
  const colors = moods.map(moodColor).filter(Boolean);
  const palette = colors.length ? colors : [cssVar("--agua")];
  const rand = mulberry32(hashSeed(id));
  const base = Math.min(width, height);
  for (let i = 0; i < blobCount(words); i += 1) {
    const x = rand() * width;
    const y = rand() * height;
    const r = base * (0.6 + rand() * 0.8);
    const color = palette[i % palette.length];
    const g = ctx.createRadialGradient(x, y, 0, x, y, r);
    g.addColorStop(0, rgba(color, 0.85));
    g.addColorStop(0.5, rgba(color, 0.4));
    g.addColorStop(1, rgba(color, 0));
    ctx.fillStyle = g;
    ctx.fillRect(x - r, y - r, r * 2, r * 2);
  }
}

// Abstract cover: blurred blobs seeded by the entry id
export const Cover = ({ id, words = 0, moods = [], width, height, className = "", testId }) => {
  const ref = useRef(null);
  const dark = useDarkScheme();
  const [measured, setMeasured] = useState({ w: width || 0, h: height || 0 });
  const moodKey = moods.join("|");

  useEffect(() => {
    if (width && height) return undefined;
    const el = ref.current;
    const ro = new ResizeObserver(([e]) => setMeasured({ w: Math.round(e.contentRect.width), h: Math.round(e.contentRect.height) }));
    ro.observe(el);
    return () => ro.disconnect();
  }, [width, height]);

  useEffect(() => {
    const w = width || measured.w;
    const h = height || measured.h;
    if (!w || !h) return;
    draw(ref.current, { id, words, moods: moodKey ? moodKey.split("|") : [], width: w, height: h });
  }, [id, words, moodKey, width, height, measured, dark]);

  return (
    <canvas ref={ref} aria-hidden="true" data-testid={testId}
      className={`block ${className}`} style={{ width: width || "100%", height: height || "100%" }} />
  );
};
